<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  ref,
  watch,
  watchEffect,
} from "vue";
import { useI18n } from "vue-i18n";
import MultiSelect from "primevue/multiselect";

const { t } = useI18n();

const emit = defineEmits([
  "update:modelValue",
  "change",
  "search",
  "scroll-end",
]);

const props = withDefaults(
  defineProps<{
    options: { option: string; value: string | number | boolean }[];
    radius?: string;
    background?: string;
    placeholder?: string;
    value?: any[];
    isEmpty?: boolean;
    loading?: boolean;
    remote?: boolean;
  }>(),
  {
    radius: "6px",
    background: "#fff",
    isEmpty: true,
    remote: false,
  }
);

const root = ref<HTMLElement | null>(null);
const scrollElement = ref<HTMLElement | null>(null);

const internalValue = ref<any[]>([]); // Alterando para array

const internalOptions = computed(() => {
  const optionEmpty = {
    label: t("common.placeholders.select"),
    value: "empty",
  };

  const optionsFormatted = (props.options || []).map((item) => ({
    label: item.option,
    value: item.value,
  }));

  return props.isEmpty ? [optionEmpty, ...optionsFormatted] : optionsFormatted;
});

function update(data: { originalEvent: Event; value: any[] }) {
  internalValue.value = data.value.filter((val) => val !== "empty"); // Remove "empty" se presente
  emit("update:modelValue", internalValue.value);
  emit("change", internalValue.value);
}

function onFilter(data: { originalEvent: Event; value: string }) {
  if (!props.remote) return;

  emit("search", data.value ?? "");
}

function findScrollElement() {
  const listId = root.value
    ?.querySelector("[aria-controls]")
    ?.getAttribute("aria-controls");

  if (!listId) return null;

  const list = document.getElementById(listId);

  return (list?.closest(".p-multiselect-items-wrapper") ??
    null) as HTMLElement | null;
}

function onScroll(event: Event) {
  if (!props.remote) return;

  const target = event.target as HTMLElement;
  const reachedEnd =
    target.scrollTop + target.clientHeight >= target.scrollHeight - 24;

  if (reachedEnd) emit("scroll-end");
}

function detachScroll() {
  scrollElement.value?.removeEventListener("scroll", onScroll);
  scrollElement.value = null;
}

async function attachScroll() {
  await nextTick();

  detachScroll();

  scrollElement.value = findScrollElement();
  scrollElement.value?.addEventListener("scroll", onScroll, {
    passive: true,
  });
}

function onShow() {
  if (props.remote) attachScroll();
}

function onHide() {
  detachScroll();
}

watch(
  () => props.options,
  (next, prev) => {
    if (!props.remote || !scrollElement.value) return;

    if (next.length < prev.length) scrollElement.value.scrollTop = 0;
  }
);

onBeforeUnmount(detachScroll);

watchEffect(() => {
  if (props.value !== undefined) internalValue.value = props.value;
});
</script>

<template>
  <div ref="root" class="base-multiselect">
    <div v-if="$slots.label" class="label">
      <slot name="label" />
    </div>
    <div class="content" :class="{ error: $slots.error }">
      <MultiSelect
        v-if="!loading"
        class="custom-multiselect"
        v-bind="$attrs"
        v-model="internalValue"
        :options="internalOptions"
        optionLabel="label"
        optionValue="value"
        :placeholder="
          placeholder ? placeholder : t('common.placeholders.select')
        "
        @change="update"
        @filter="onFilter"
        @show="onShow"
        @hide="onHide"
        display="chip"
        :filter="true"
        :showClear="true"
      >
      </MultiSelect>
    </div>
    <div v-if="$slots.error" class="error-message">
      <slot name="error" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
$radius: v-bind("radius");
$background: v-bind("background");

.base-multiselect {
  display: flex;
  width: inherit;
  flex-direction: column;
  gap: 2px;

  .label {
    font-size: 0.875rem;
    margin-block-end: 0.2rem;
    margin-inline: 0.5rem;
  }

  .content {
    position: relative;
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: center;
    border-radius: $radius;
    background: $background;

    &.error {
      border-color: red;
    }
  }

  .error-message {
    position: relative;
    margin-block-end: -1.25rem;
    text-align: end;
    font-size: 0.8rem;
    padding-inline: $radius;
    color: red;
  }

  .custom-multiselect {
    position: relative;
    display: flex;
    align-items: center;
    height: 44px;
  }
}

:deep(.p-multiselect) {
  width: calc(100% - 2px);
  border: none;
  border-radius: $radius;
  border: 1px solid #d9d9d9;
}

:deep(.p-placeholder) {
  outline: none;
  color: var(--text-medium);
}
</style>
